import { NgModule } from '@angular/core';
import { RouterModule, Routes, CanActivate, Router } from '@angular/router';

import { LoginComponent } from './login/login.component';
import { AssignmentComponent } from './assignments/assignment-create.component';
import { AssignmentsComponent } from './assignments/assignment-list.component';
import { ImportComponent } from './import/import.component';
import { RouterGuard } from './router.guard';
import { SlideshowComponent } from './slideshow/slideshow.component';
import { SlideComponent } from './slideshow/slide/slide.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { PageNotFoundComponent } from './dashboard/not-found.component';
import { UserCreateComponent } from './users/user-create.component';
import {UsersComponent} from './users/user-list.component';
import {BannedWordsComponent} from './banned-words/banned-words.component';
import {EditTaxonomiesComponent} from './taxonomies/edit-taxonomies.component';
import {EditTechniquesComponent} from './techniques/edit-techniques.component';
import {GettyWindowComponent} from './getty/getty-window.component';
import {DisclosuresComponent} from './disclosures/disclosures.component';
import {VerticalsComponent} from './verticals/verticals.component';
import {ProfilesComponent} from './profiles/profiles/profiles.component'
import { BulkCloneComponent } from './bulk-clone/bulk-clone.component';

const routes: Routes = [
    { 
        path: '',
        component: DashboardComponent,
        canActivate: [RouterGuard],
        children: [
            {
                path: '', //    default to same as /assignments/queue
                component: AssignmentsComponent,
                data: {
                    state: 'queue',
                    title: 'My Queue'
                }
            },
            {
                path: 'assignment/create',
                component: AssignmentComponent,
                data: {
                    state: 'assignment/create',
                    roles: ['editor', 'administrator'],
                    title: 'Create Assignment'
                }
            },
            {
                path: 'assignment/create/summary',
                component: AssignmentComponent,
                data: {
                    state: 'assignment/create/summary',
                    roles: ['editor', 'administrator'],
                    title: 'Create Summary Assignment'
                }
            },
            {
                path: 'assignment/create/quiz',
                component: AssignmentComponent,
                data: {
                    state: 'assignment/create/quiz',
                    roles: ['editor', 'administrator'],
                    title: 'Create Quiz Assignment'
                }
            },
            {
                path: 'assignment/create/rsoc',
                component: AssignmentComponent,
                data: {
                    state: 'assignment/create/rsoc',
                    roles: ['editor', 'administrator'],
                    title: 'Create RSOC Assignment'
                }
            },
            {
                path: 'import',
                component: ImportComponent,
                data: {
                    state: 'import',
                    roles: ['editor', 'administrator'],
                    title: 'Import Assignment',
                    limited: true
                }
            },
            {
                path: 'assignments/queue',
                component: AssignmentsComponent,
                data: {
                    state: 'queue',
                    title: 'My Queue'
                }
            },
            {
                path: 'assignments/sent-to-editor',
                component: AssignmentsComponent,
                data: {
                    state: 'sentToEditor',
                    title: 'Sent to Editor',
                    roles: ['editor', 'administrator']
                }
            },
            {
                path: 'assignments/syn-sent-to-editor',
                component: AssignmentsComponent,
                data: {
                    state: 'synSentToEditor',
                    title: 'Syndication Sent To Editor',
                    roles: ['editor', 'administrator']
                }
            },
            {
                path: 'assignments/ready-to-delete-an',
                component: AssignmentsComponent,
                data: {
                    state: 'readyToDeleteAppleNews',
                    title: 'Ready to Delete Apple News',
                    roles: ['editor', 'administrator']
                }
            },
            {
                path: 'assignments/ready-to-repost',
                component: AssignmentsComponent,
                data: {
                    state: 'readyToRepost',
                    title: 'Ready to Repost',
                    roles: ['editor', 'administrator']
                }
            },
            {
                path: 'assignments/assigned',
                component: AssignmentsComponent,
                data: {
                    state: 'assigned',
                    title: 'Assigned',
                    roles: ['editor', 'administrator']
                }
            },
            {
                path: 'assignments',
                component: AssignmentsComponent,
                data: {
                    state: 'assignments',
                    title: 'All Assignments',
                    roles: ['editor', 'administrator']
                }
            },
            {
                path: 'tools', 
                redirectTo: '/', 
                pathMatch: 'full'
            },
            {
                path: 'tools/bulk-clone',
                component: BulkCloneComponent,
                data: {
                    state: 'bulk-clone',
                    title: 'Bulk Clone',
                    roles: ['editor', 'administrator']
                }
            },
            {
                path: 'slideshow/:id',
                component: SlideshowComponent,
                data: {
                    state: 'slideshow',
                    title: 'Slideshow'
                },
                children: [
                    {
                        path: ':slide',
                        component: SlideComponent,
                        data: {
                            state: 'slide',
                            title: 'Slideshow'
                        }
                    }
                ]      
            },
            {
                path: 'users/create',
                component: UserCreateComponent,
                data: {
                    state: 'users/create',
                    roles: ['administrator'],
                    title: 'Create Users'
                }       
            },
            {
                path: 'users',
                component: UsersComponent,
                data: {
                    state: 'users',
                    roles: ['administrator'],
                    title: 'All Users'
                }   
            },
            {
                path: 'users/profile/:id',
                component: ProfilesComponent,
                data: {
                    state: 'users',
                    roles: ['administrator'],
                    title: 'All Users'
                }   
            },
            {
                path: 'settings/banned-words',
                component: BannedWordsComponent,
                data: {
                    state: 'banned-words',
                    roles: ['editor', 'administrator'],
                    title: 'Banned Words'
                }
            },
            {
                path: 'settings/taxonomies',
                component: EditTaxonomiesComponent,
                data: {
                    state: 'settings/taxonomies',
                    roles: ['editor', 'administrator'],
                    title: 'Taxonomies'
                }
            },
            {
                path: 'settings/techniques',
                component: EditTechniquesComponent,
                data: {
                    state: 'settings/techniques',
                    roles: ['editor', 'administrator'],
                    title: 'Techniques'
                }
            },
            {
                path: 'settings/disclosures',
                component: DisclosuresComponent,
                data: {
                    state: 'settings/disclosures',
                    roles: ['editor', 'administrator'],
                    title: 'Disclosures'
                }
            },
            {
                path: 'settings/verticals',
                component: VerticalsComponent,
                data: {
                    state: 'settings/verticals',
                    title: 'Verticals'
                }
            } 
        ]
    },
    {
        path: 'getty',
        component: GettyWindowComponent,
        data: {
            state: 'getty',
            title: 'Getty Search | SWARM'
        }
    },
    {
        path: '**',
        component: PageNotFoundComponent
    }
];

@NgModule({
    imports: [ RouterModule.forRoot(routes) ],
    exports: [ RouterModule ]
})
export class AppRoutesModule {}